import { Select as BaseSelect } from '@base-ui-components/react/select';
import { createContext, useContext } from 'react';
import { cn } from '../cn.js';
import { IconCheck, IconChevronDown } from '../icons.js';

/** What each value is called: the same labels the items show, for the trigger to show. */
export type SelectItems =
  | Readonly<Record<string, React.ReactNode>>
  | ReadonlyArray<{ readonly value: unknown; readonly label: React.ReactNode }>;

const SelectItemsContext = createContext<SelectItems | undefined>(undefined);

export type SelectProps<Value, Multiple extends boolean | undefined = false> = Omit<
  BaseSelect.Root.Props<Value, Multiple>,
  'items'
> & {
  /**
   * The label of every value, which is what the closed trigger shows.
   *
   * **Required**, because the trigger cannot read the labels off the items: they live in the popup,
   * which has not mounted until somebody opens it. Without this every select in the product showed
   * its stored value — `longestIdle`, `en`, `comfortable` — instead of its name (M5.5-01).
   */
  readonly items: SelectItems;
};

/**
 * A select. Base UI owns the listbox semantics, typeahead, and keyboard navigation; this file
 * gives it the product's shape in tokens.
 */
export function Select<Value, Multiple extends boolean | undefined = false>({
  items,
  ...props
}: SelectProps<Value, Multiple>) {
  return (
    <SelectItemsContext.Provider value={items}>
      <BaseSelect.Root<Value, Multiple>
        items={items as BaseSelect.Root.Props<Value, Multiple>['items']}
        {...props}
      />
    </SelectItemsContext.Provider>
  );
}

function labelOf(items: SelectItems | undefined, value: unknown): React.ReactNode {
  if (items === undefined) {
    return value as React.ReactNode;
  }
  if (Array.isArray(items)) {
    const found = (items as Extract<SelectItems, ReadonlyArray<unknown>>).find(
      (item) => item.value === value,
    );
    return found === undefined ? (value as React.ReactNode) : found.label;
  }
  const record = items as Readonly<Record<string, React.ReactNode>>;
  return Object.hasOwn(record, String(value)) ? record[String(value)] : (value as React.ReactNode);
}

export interface SelectValueProps extends BaseSelect.Value.Props {
  /** Shown when nothing is selected yet. */
  placeholder?: string;
}

/** The selected label, or the placeholder while the selection is empty. */
export function SelectValue({ placeholder, className, ...props }: SelectValueProps) {
  const items = useContext(SelectItemsContext);
  return (
    <BaseSelect.Value className={cn('truncate', className)} {...props}>
      {(value: unknown) =>
        value === null || value === undefined || value === '' ? (
          <span className="text-(--input-placeholder)">{placeholder}</span>
        ) : (
          labelOf(items, value)
        )
      }
    </BaseSelect.Value>
  );
}

export function SelectTrigger({ className, children, ...props }: BaseSelect.Trigger.Props) {
  return (
    <BaseSelect.Trigger
      className={cn(
        'inline-flex w-full cursor-pointer items-center justify-between gap-(--space-sm)',
        'h-(--input-height-md) rounded-(--input-radius) px-(--input-padding-inline)',
        'bg-(--color-surface) text-(length:--font-size-body) text-(--color-text-primary)',
        'border border-(--input-border-color)',
        'outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring) focus-visible:ring-offset-2 focus-visible:ring-offset-(--color-surface)',
        'data-disabled:opacity-(--opacity-disabled) data-disabled:cursor-not-allowed',
        'data-[popup-open]:border-(--color-accent)',
        className,
      )}
      {...props}
    >
      {children}
      <BaseSelect.Icon>
        <IconChevronDown
          aria-hidden="true"
          className="size-(--size-icon-sm) opacity-(--opacity-muted)"
        />
      </BaseSelect.Icon>
    </BaseSelect.Trigger>
  );
}

export function SelectContent({ className, children, ...props }: BaseSelect.Popup.Props) {
  return (
    <BaseSelect.Portal>
      <BaseSelect.Positioner sideOffset={4} className="z-(--elevation-dropdown)">
        <BaseSelect.Popup
          className={cn(
            'min-w-(--anchor-width) max-h-72 overflow-y-auto',
            'rounded-(--radius-lg) p-(--space-xs)',
            'bg-(--color-surface-elevated) text-(--color-text-primary)',
            'border border-(--color-border) shadow-(--shadow-popover)',
            'transition duration-(--motion-duration-fast) ease-(--motion-easing-standard)',
            'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
            'motion-reduce:transition-none',
            className,
          )}
          {...props}
        >
          {children}
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  );
}

export function SelectItem({ className, children, ...props }: BaseSelect.Item.Props) {
  return (
    <BaseSelect.Item
      className={cn(
        'flex cursor-pointer items-center justify-between gap-(--space-sm)',
        'rounded-(--radius-md) px-(--space-sm) py-(--space-xs)',
        'text-(length:--font-size-body)',
        'data-[highlighted]:bg-(--color-accent-subtle) data-[highlighted]:text-(--color-accent)',
        'data-[disabled]:opacity-(--opacity-disabled) data-[disabled]:cursor-not-allowed',
        'outline-none',
        className,
      )}
      {...props}
    >
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
      <BaseSelect.ItemIndicator>
        <IconCheck aria-hidden="true" className="size-(--size-icon-sm)" />
      </BaseSelect.ItemIndicator>
    </BaseSelect.Item>
  );
}
